import React, { useCallback, useEffect } from "react"
import { useTheme, Box } from "@mui/material"
import { get, isNil } from "lodash"

import { GTM_AMENITY_SEARCH, Q_MAP_VIEW, Q_ORDINAL } from "constant"
import { pushDataLayer } from "utils/googletagmanager"
import { convertMapViewObjectToPath } from "utils/map"

import { useVenue } from "providers/venue"
import { useMap } from "hooks/map/useMap"
import { useHighLightFeaturesMap } from "hooks/feature/useHighLightFeaturesMap"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useFeatureCategory } from "hooks/feature"
import { useSearchParams } from "react-router-dom"
import { useGeoLocation } from "providers/geolocation"
import {
  useFlyToMap,
  useGetUserLocationView,
  useGetMapViewFromUrl,
} from "hooks/map"

import { PageCard } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { MyLocationButton } from "components/Button"

import { SidebarMenu } from "extensions/iconsiam/components/navigation/kiosk-h/SidebarMenu"
import { KioskOrdinalSelector } from "extensions/iconsiam/components/ordinal-selector"
import AttractionButton from "extensions/iconsiam/components/domains/attractions/AttractionButton"
import { FeatureToggleChipTabBar } from "components/domains/features"

export const FeatureByCategoryKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}) => {
  const { userLocation, locationVenue, locationOrdinal } = useGeoLocation()
  const locationVenueId = get(locationVenue, "id")
  const [searchParams, setSearchParams] = useSearchParams()
  const { isMultiOrdinalVenue, dataLoaded } = useVenue()
  const { sortedLevels } = useMap()

  const {
    viewingCategory,
    viewingVenueId,
    viewingOrdinal,
    comingFromOrdinal,
    featureCategories,
    searchableFeatures,
    handleClickQuickSearch,
    handleOrdinalChange,
  } = useFeatureCategory({
    initialVenueId: locationVenueId,
    initialOrdinal: locationOrdinal,
  })

  const userLocationView = useGetUserLocationView()
  const { mapView } = useGetMapViewFromUrl()
  useFlyToMap(mapView)
  const theme = useTheme()

  const enableMap =
    !isNil(viewingOrdinal) && isNil(comingFromOrdinal) && dataLoaded

  const { styleOverrides = {} } = theme.components[`AppMap`] || {}
  const {
    quickLinkContainer,
    ordinalSelector,
    toolbarContainer,
    locationButtonContainer,
    attractionButtonContainer,
  } = styleOverrides

  const handleClickMyLocation = useCallback(() => {
    //This condition prevent navigation when ordinal is undefined
    if (!userLocation) return
    const userOrdinal = get(userLocation, "properties.ordinal", 0)
    const mapViewPath = convertMapViewObjectToPath(userLocationView)
    searchParams.set(Q_ORDINAL, userOrdinal)
    searchParams.set(Q_MAP_VIEW, mapViewPath)
    setSearchParams(searchParams.toString())
  }, [setSearchParams, searchParams, userLocationView, userLocation])

  //@ts-ignore
  useHighLightFeaturesMap(searchableFeatures, Number(viewingOrdinal), {
    enabled: enableMap,
  })

  // Run on viewingCategory change and send event to GA
  useEffect(() => {
    if (viewingCategory) {
      pushDataLayer({
        event: GTM_AMENITY_SEARCH,
        amenity_category: viewingCategory,
      })
    }
  }, [viewingCategory])

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  return (
    //@ts-ignore
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <Box sx={toolbarContainer}>
        <Box sx={quickLinkContainer}>
          <FeatureToggleChipTabBar
            categories={featureCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
      </Box>

      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <KioskOrdinalSelector
            selectedOrdinal={Number(viewingOrdinal)}
            levels={sortedLevels}
            onChangeOrdinal={handleOrdinalChange}
            currentVenue={locationVenueId}
            viewingVenueId={viewingVenueId}
            locationOrdinal={locationOrdinal}
          />
        </Box>
      )}

      {userLocation && (
        <Box sx={locationButtonContainer}>
          <MyLocationButton onClick={handleClickMyLocation} />
        </Box>
      )}
      <Box sx={attractionButtonContainer}>
        <AttractionButton />
      </Box>
    </Layout>
  )
}
